import type { UseQueryResult } from 'react-query'
import { useContext, useEffect, useMemo, useState } from 'react'
import { Connection, Organization } from '@yuno/types'
import {
  useGetConnections,
  useGetProviderParams,
  usePostConnectionMultiAccount,
  useGetPaymentsMethodsByCountry,
  useNewPostConnectionValidate,
  queryClient,
} from '@yuno/dashboard-api-mfe'
import { useAccount, ContainerContext } from '@yuno/dashboard-container-mfe'
import {
  ConstantText,
  providerLocationCoverage,
  textSearch,
  yunoTestId,
  yunoTestName,
} from '../utils/constants'
import { storage } from '@yuno/dashboard-container-mfe'
import {
  IInstructions,
  ITestKey,
  SetupInstructions,
} from '../interfaces/connection.interfaces'
import { FiltersContext } from '../context/filters-context'
const minSize = 0
interface ISelectedProvider {
  account_name?: string
  payment_method?: string
  id: string
  name: string
  icon: string
  description: string
  fields: any
  countries: string
  paymentMethods: Organization.PaymentMethods[]
  category: string
  setup_instructions: Array<SetupInstructions>
  instructions: IInstructions[]
  test_parameters?: ITestKey[]
}
const useConnect = () => {
  const [openDrawerForm, setOpenDrawerForm] = useState<boolean>(false)
  const [selectedProvider, setSelectedProvider] = useState<ISelectedProvider>()
  const [drawerData, setDrawerData] = useState<ISelectedProvider>()
  const [paymentMethods, setpaymentMethods] =
    useState<Organization.PaymentMethods[]>()
  const [params, setParams] = useState<Organization.PaymentMethods>()
  const {
    filter: { countryCoverageCode, categoryType, search },
  } = useContext(FiltersContext)
  const [connectionsFiltered, setConnectionsFiltered] = useState([])

  const paymentMethodsResponse: UseQueryResult<
    Organization.PaymentMethods[],
    unknown
  > = useGetPaymentsMethodsByCountry(params)
  const {
    data: connections,
    loading,
    isLoading,
    isFetching,
  }: UseQueryResult<Connection.ConnectionsList, unknown> = useGetConnections()
  const postConnection = usePostConnectionMultiAccount()
  const postValidateAccounts = useNewPostConnectionValidate()
  const accounts = queryClient.getQueryState(['accounts'])?.data
  const actualAccount = useAccount()
  const [validAccounts, setValidAccounts] = useState(null)
  const [validating, setValidating] = useState(false)
  const [valuesForm, setValuesForm] = useState()
  const [integrationTypes, setintegrationTypes] = useState([])
  const idProvider = useMemo(() => selectedProvider?.id, [selectedProvider?.id])
  const providerResponse: UseQueryResult<
    Organization.IntegrationParams[],
    unknown
  > = useGetProviderParams({ id: idProvider })
  useEffect(() => {
    if (
      categoryType?.length === minSize &&
      countryCoverageCode?.length === minSize &&
      search.length === minSize
    ) {
      setConnectionsFiltered(connections)
    } else {
      const newConnectionsByCategory = connections?.filter((connection) => {
        const hasValidCategory =
          categoryType.length === minSize ||
          categoryType.includes(connection.category)
        const hasValidCountries =
          countryCoverageCode.length === minSize ||
          connection.location_coverage.some((c) =>
            countryCoverageCode.includes(c),
          )
        const hasValidSearch =
          search.length === minSize ||
          textSearch(connection.name).includes(textSearch(search))
        return hasValidCategory && hasValidCountries && hasValidSearch
      })
      const yunoTest =
        textSearch(yunoTestName).includes(textSearch(search)) &&
        countryCoverageCode?.length > minSize
          ? connections?.filter((connection) => connection.id === yunoTestId)
          : []
      setConnectionsFiltered([...newConnectionsByCategory, ...yunoTest])
    }
  }, [countryCoverageCode, categoryType, connections, search])
  useEffect(() => {
    if (providerResponse.status === 'success') {
      const fields = providerResponse.data
      setDrawerData({
        ...selectedProvider,
        countries: providerLocationCoverage?.[selectedProvider?.id],
        fields: fields.params,
        paymentMethods: fields.paymentMethods,
      })
      handleOpenDrawer()
    }
  }, [providerResponse.status])
  useEffect(() => {
    if (selectedProvider && Object.keys(selectedProvider).length > minSize) {
      providerResponse.refetch()
    }
  }, [selectedProvider])
  useEffect(() => {
    idProvider == 'PAYMENTEZ'
      ? setpaymentMethods(paymentMethodsResponse.data)
      : setpaymentMethods(drawerData?.paymentMethods)
  }, [providerResponse])
  const setProviderId = (id?: string) => {
    setSelectedProvider(connections.find((el) => el.id === id))
  }
  const handleOpenDrawer = () => {
    setOpenDrawerForm(true)
  }
  const resetForm = () => {
    providerResponse.remove()
    postConnection.reset()
    postValidateAccounts.reset()
    setValidating(false)
  }
  const handleCloseDrawer = () => {
    storage.removeItem('tempValuesForm')
    setOpenDrawerForm(false)
    setSelectedProvider(null)
    resetForm()
  }
  const handleChangeCountry = (e) => {
    setParams({
      provider_id: idProvider,
      country: e.target.value,
    })
  }

  const findCheckboxParams = (param_id, param) => {
    if (param.field_type === 'MULTIPLE_CHECK' && param.param_id === param_id) {
      return true
    }
    let paramValue = false
    param.params &&
      param.params.forEach((p) => {
        let paramValueFor = false
        paramValueFor = findCheckboxOptions(param_id, p)
        if (paramValueFor) {
          paramValue = true
        }
      })
    return paramValue
  }

  const findCheckboxOptions = (param_id, param) => {
    if (param.field_type === 'MULTIPLE_CHECK' && param.param_id === param_id) {
      return true
    }
    let paramOption = false
    param.options &&
      param.options.forEach((p) => {
        let paramOptionValue = false
        paramOptionValue = findCheckboxParams(param_id, p)
        if (paramOptionValue) {
          paramOption = true
        }
      })
    return paramOption
  }

  const isCheckbox = (param_id) => {
    if (
      providerResponse.data.new_params_catalog.length === 0 ||
      !providerResponse.data.new_params_catalog
    ) {
      return false
    }
    let hasCheckbox = false
    providerResponse.data.new_params_catalog.forEach((param) => {
      let findCheckboxParam = false
      findCheckboxParam = findCheckboxOptions(param_id, param)
      if (findCheckboxParam) {
        hasCheckbox = true
      }
    })
    return hasCheckbox
  }

  const returnPaymentMethods = (element) => {
    if (element) {
      let stringReturn = ''
      Object.entries(element).map((el) => {
        if (el[1]) {
          stringReturn += `${el[0]},`
        }
      })
      return stringReturn
    }
  }

  const handleSubmit = (data) => {
    setValuesForm(data)
    const codeAccounts = accounts.map((account) => account.code)
    let connection = {
      accounts: codeAccounts,
      provider_id: selectedProvider.id,
      ...data,
      params: data.params.filter((el) => {
        const isCheck = isCheckbox(el.param_id)
        const response = isCheck ? el
            : el.value !== ''
        return response
      }).map((el)=> {
        const isCheck = isCheckbox(el.param_id)
        const response = isCheck ? { ...el, value: returnPaymentMethods(el.value)}
            : el
        return response
      }),
    }
    postValidateAccounts.mutate({ payload: connection })
  }
  const handleSubmitConfirm = (data) => {
    if (data.default) {
      let connectionDefault = {
        ...postValidateAccounts.variables.payload,
        accounts: [actualAccount.code],
      }
      postConnection.mutate({ payload: connectionDefault })
    } else {
      const accountArray = [actualAccount.code]
      Object.entries(data).forEach((account) => {
        return account[1] && accountArray.push(account[minSize])
      })
      let connection = {
        ...postValidateAccounts.variables.payload,
        accounts: accountArray,
      }
      postConnection.mutate({ payload: connection })
    }
  }
  const handleClickReturn = () => {
    setValidating(false)
  }
  useEffect(() => {
    if (postValidateAccounts.isSuccess) {
      const validAccounts = []
      postValidateAccounts.data.forEach((code) => {
        code !== actualAccount.code &&
          validAccounts.push(accounts.find((account) => account.code === code))
      })
      setValidAccounts(validAccounts)
      if (
        postValidateAccounts.data.length === 1 &&
        actualAccount.code === postValidateAccounts.data[minSize]
      ) {
        const payload = {
          ...postValidateAccounts.variables.payload,
          accounts: postValidateAccounts.data,
        }
        postConnection.mutate({ payload })
        setValidating(false)
      } else if (validAccounts.length > minSize) {
        setValidating(true)
      }
      if (postValidateAccounts.data.length === minSize) {
        const payload = {
          ...postValidateAccounts.variables.payload,
          accounts: [actualAccount.code],
        }
        postConnection.mutate({ payload })
        setValidating(false)
      }
    }
  }, [postValidateAccounts.isSuccess])
  useEffect(() => {
    if (postConnection.isError) {
      setValidating(false)
    }
  }, [postConnection.isError])
  useEffect(() => {
    if (postConnection.isSuccess) {
      setValidating(false)
    }
  }, [postConnection.isSuccess])
  useEffect(() => {
    if (
      providerResponse.data &&
      providerResponse?.data?.provider?.integration_types
    ) {
      const integrationTypes =
        providerResponse?.data?.provider?.integration_types
      setintegrationTypes(integrationTypes)
    }
  }, [providerResponse])
  return {
    openDrawerForm,
    drawerData,
    handleSubmit,
    handleCloseDrawer,
    setProviderId,
    postConnection,
    connections: connectionsFiltered,
    isLoadingConnection: isLoading || isFetching,
    paymentMethods,
    resetForm,
    handleChangeCountry,
    providerResponse,
    validating,
    validAccounts,
    postValidateAccountsSuccess: postValidateAccounts.isSuccess,
    postValidateAccountsError: postValidateAccounts.isError,
    handleSubmitConfirm,
    actualAccount,
    handleClickReturn,
    valuesForm,
    integrationTypes,
  }
}
export default useConnect
